/**
 * Cosmetics v3 solutions slider: vanilla custom element, no dependencies.
 *
 * Moves a flex track with transform. With looping on, presentation copies of the real cards sit before
 * and after the track. Copies repeat existing cards only, so no product or collection data is duplicated.
 * A move that settles on a copy is followed by an instant jump to the real card with the same look, so
 * Next always moves forward and Previous always moves back. Copies are removed from keyboard and assistive
 * technology, and they keep their links, which open the same destinations as the real cards.
 */
const SWIPE_THRESHOLD = 40;
const CLICK_GUARD_MS = 400;

class Cv3SolutionsSlider extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.viewport = this.querySelector('[data-cv3-sol-viewport]');
    this.track = this.querySelector('[data-cv3-sol-track]');
    this.prev = this.querySelector('[data-cv3-sol-prev]');
    this.next = this.querySelector('[data-cv3-sol-next]');
    this.status = this.querySelector('[data-cv3-sol-status]');
    this.nav = this.querySelector('.cv3-sol__nav');

    // Reconnecting must not stack a second set of copies.
    this.track?.querySelectorAll('[data-cv3-sol-clone]').forEach((node) => node.remove());
    this.items = this.track ? [...this.track.querySelectorAll(':scope > [data-cv3-sol-item]')] : [];
    this.total = this.items.length;
    if (!this.track || this.total === 0) return;

    this.enabled = this.dataset.slider === 'true';
    this.loop = this.enabled && this.dataset.loop === 'true' && this.total > 1;
    this.swipe = this.dataset.swipe === 'true';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.direction = document.documentElement.dir === 'rtl' ? -1 : 1;
    this.drag = null;
    this.suppressClickUntil = 0;
    this.lead = 0;
    this.trail = 0;
    this.allSlides = [...this.items];
    this.pos = 0;

    if (this.loop) this.buildCopies();
    this.pos = this.lead;

    this.prev?.addEventListener('click', () => this.step(-1), { signal });
    this.next?.addEventListener('click', () => this.step(1), { signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.track.addEventListener('transitionend', (event) => this.onTransitionEnd(event), { signal });

    if (this.enabled && this.swipe && this.viewport) {
      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
      this.viewport.addEventListener('pointercancel', () => { this.drag = null; }, { signal });
      // A native image drag would cancel the swipe, so the carousel never starts one.
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      // The browser fires a click after a drag. That click must not move the carousel back.
      this.viewport.addEventListener('click', (event) => {
        if (performance.now() >= this.suppressClickUntil) return;
        event.preventDefault();
        event.stopPropagation();
      }, { capture: true, signal });
    }

    // Theme Editor: selecting a card brings it into view.
    document.addEventListener('shopify:block:select', (event) => {
      const item = event.target?.closest?.('[data-cv3-sol-item]');
      if (!item || !this.contains(item)) return;
      this.goTo(this.items.indexOf(item));
    }, { signal });

    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(this.track);
    this.layout();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
  }

  // Visible cards at the current breakpoint (fractional values show a peek of the next card).
  visibleCount() {
    const width = window.innerWidth;
    if (width >= 1024) return Number(this.dataset.visibleD) || 3;
    if (width >= 750) return Number(this.dataset.visibleT) || 2.5;
    return Number(this.dataset.visibleM) || 1.1;
  }

  // The largest count across breakpoints, so copies always cover the widest layout.
  widestVisible() {
    return Math.max(Number(this.dataset.visibleD) || 3, Number(this.dataset.visibleT) || 2.5, Number(this.dataset.visibleM) || 1.1);
  }

  // Largest logical index that keeps the last card in view. Used only without looping.
  maxIndex() {
    return Math.max(0, this.total - Math.floor(this.visibleCount()));
  }

  // Copies: one before the real cards, and enough after them to fill the widest viewport.
  buildCopies() {
    this.lead = 1;
    this.trail = Math.ceil(this.widestVisible()) + 1;
    const n = this.total;
    const copy = (source, index) => {
      const node = source.cloneNode(true);
      node.removeAttribute('data-cv3-sol-item');
      node.removeAttribute('id');
      node.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      node.dataset.cv3SolClone = '';
      node.dataset.index = String(index);
      node.setAttribute('aria-hidden', 'true');
      node.querySelectorAll('a, button, input, select, textarea').forEach((el) => el.setAttribute('tabindex', '-1'));
      return node;
    };
    const leading = [];
    for (let s = 0; s < this.lead; s++) {
      const real = (((s - this.lead) % n) + n) % n;
      leading.push(copy(this.items[real], real));
    }
    const trailing = [];
    for (let j = 0; j < this.trail; j++) {
      trailing.push(copy(this.items[j % n], j % n));
    }
    leading.forEach((node) => this.track.insertBefore(node, this.items[0]));
    trailing.forEach((node) => this.track.append(node));
    this.allSlides = [...leading, ...this.items, ...trailing];
  }

  // The real card that the track position shows.
  get index() {
    if (!this.loop) return this.pos;
    return ((((this.pos - this.lead) % this.total) + this.total) % this.total);
  }

  // Distance between neighbouring cards, including the gap.
  get stride() {
    const a = this.allSlides[0];
    const b = this.allSlides[1];
    if (!a || !b) return 0;
    return Math.abs(b.offsetLeft - a.offsetLeft);
  }

  // Whether arrows can move the carousel at all.
  canMove() {
    if (!this.enabled || this.total < 2) return false;
    return this.loop || this.maxIndex() > 0;
  }

  layout() {
    if (!this.enabled) {
      this.updateStatus(1, this.total);
      return;
    }
    const needed = this.canMove();
    if (this.nav) this.nav.hidden = !needed;
    if (this.status) this.status.hidden = !needed;
    if (!this.loop) {
      const max = this.maxIndex();
      if (this.pos - this.lead > max) this.pos = max;
    }
    this.apply(true);
  }

  // Positions the track so the current position is at the start edge.
  apply(instant) {
    const stride = this.stride;
    // A single card has nothing to move to, so it stays at rest.
    const offset = stride ? -this.pos * stride * this.direction : 0;
    if (instant || this.reducedMotion) this.track.style.transition = 'none';
    this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
    if (instant || this.reducedMotion) {
      void this.track.offsetWidth;
      this.track.style.transition = '';
    }
    // Without a transition there is no transitionend, so settle the position straight away.
    if (!instant && this.reducedMotion && this.loop) this.normalize();
    this.syncControls();
  }

  // After a move settles on a copy, jump to the real card. The copy looks the same, so nothing moves visibly.
  onTransitionEnd(event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;
    this.normalize();
  }

  normalize() {
    if (!this.loop) return;
    const n = this.total;
    while (this.pos < this.lead) this.pos += n;
    while (this.pos >= this.lead + n) this.pos -= n;
    this.apply(true);
  }

  // The track position closest to the current one that shows the requested real card.
  slotFor(index) {
    const options = [];
    for (let k = -2; k <= 2; k++) options.push(this.lead + index + k * this.total);
    return options.reduce((best, slot) => (Math.abs(slot - this.pos) < Math.abs(best - this.pos) ? slot : best));
  }

  goTo(index) {
    if (!this.canMove() || index < 0 || index >= this.total || index === this.index) return;
    this.pos = this.loop ? this.slotFor(index) : index;
    this.apply(false);
    this.updateStatus(this.index + 1, Math.min(this.total, this.index + Math.floor(this.visibleCount())));
  }

  syncControls() {
    const needed = this.canMove();
    if (this.prev && this.next) {
      const max = this.maxIndex();
      this.prev.disabled = !needed || (!this.loop && this.index <= 0);
      this.next.disabled = !needed || (!this.loop && this.index >= max);
    }
    const visible = Math.floor(this.visibleCount());
    const start = this.index + 1;
    const end = Math.min(this.total, this.index + visible);
    this.updateStatus(start, end);
  }

  updateStatus(start, end) {
    if (!this.status) return;
    const template = this.dataset.statusTemplate || '';
    this.status.textContent = template
      .replace('#START#', String(start))
      .replace('#END#', String(end))
      .replace('#TOTAL#', String(this.total));
  }

  step(direction) {
    if (!this.canMove()) return;
    if (this.loop) {
      // Presentation copies make every move valid. Only the position changes, never the data.
      this.pos += direction;
      this.apply(false);
      return;
    }
    const max = this.maxIndex();
    const target = Math.min(max, Math.max(0, this.pos + direction));
    if (target === this.pos) return;
    this.pos = target;
    this.apply(false);
  }

  onKeydown(event) {
    if (!this.enabled) return;
    if (event.target.closest('input, textarea, select')) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    // ArrowRight moves toward the visual right. In RTL, the logical "next" is visually left.
    const towardEnd = event.key === 'ArrowRight' ? 1 : -1;
    event.preventDefault();
    this.step(towardEnd * this.direction);
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.drag = { x: event.clientX, y: event.clientY };
  }

  onPointerUp(event) {
    if (!this.drag) return;
    const rawDx = event.clientX - this.drag.x;
    const dx = rawDx * this.direction;
    const dy = Math.abs(event.clientY - this.drag.y);
    this.drag = null;
    // Any real movement that ends on a card link would otherwise open that link as a click.
    if (Math.abs(rawDx) > 6 || dy > 6) this.suppressClickUntil = performance.now() + CLICK_GUARD_MS;
    if (dy > Math.abs(dx) || Math.abs(dx) < SWIPE_THRESHOLD) return;
    // A negative dx means the finger moved toward the logical start, so advance.
    this.step(dx < 0 ? 1 : -1);
  }
}

if (!customElements.get('cv3-solutions-slider')) {
  customElements.define('cv3-solutions-slider', Cv3SolutionsSlider);
}
